import {useCallback, useRef} from 'react';
import {useNavigate} from 'react-router-dom';

import {parsePath, getNormalizedPath} from '~ui/utils';

export function useRouterClick() {
    const navigate = useNavigate();
    const cleanUpRef = useRef<(() => void) | null>(null);

    const handleClick = useCallback(
        (event: MouseEvent) => {
            // old ui doesn't support BrowserHistory
            if (!navigate) {
                return;
            }

            if (event.defaultPrevented) {
                return;
            }

            const comboKey = event.metaKey || event.altKey || event.ctrlKey || event.shiftKey;

            if (comboKey || event.button !== 0) {
                return;
            }

            const anchor = event.target instanceof HTMLElement ? event.target.closest('a') : null;

            if (!anchor) {
                return;
            }

            if (anchor.target && anchor.target !== '_self') {
                return;
            }

            if (anchor.getAttribute('download')) {
                return;
            }

            const href = anchor.getAttribute('href');

            if (!href || href.startsWith('#')) {
                return;
            }

            const parsedUrl = new URL(href, window.location.href);

            if (
                parsedUrl.protocol !== window.location.protocol ||
                parsedUrl.host !== window.location.host
            ) {
                return;
            }

            // current page in link may be with hash
            if (
                getNormalizedPath(parsedUrl.pathname) ===
                    getNormalizedPath(window.location.pathname) &&
                parsedUrl.hash
            ) {
                return;
            }

            const {handler} = parsePath(parsedUrl.pathname);

            // edit for switch to the old interface
            // files we should download
            if (handler === '.edit' || /\.files(?:\/.*|$)/.test(handler)) {
                return;
            }

            if (anchor.rel && /(?:^|\s+)external(?:\s+|$)/.test(anchor.rel)) {
                return;
            }

            event.preventDefault();
            navigate(getNormalizedPath(parsedUrl.pathname + parsedUrl.search + parsedUrl.hash));
        },
        [navigate],
    );

    return useCallback(
        (container: HTMLElement | null) => {
            if (!container) {
                cleanUpRef.current?.();
                cleanUpRef.current = null;
                return;
            }

            container.addEventListener('click', handleClick);

            cleanUpRef.current = () => {
                container.removeEventListener('click', handleClick);
            };
        },
        [handleClick],
    );
}
